import { http, type HttpTransportConfig, type Transport } from 'viem'

import type { ChainId } from '../web3.types'

import { getAlchemyMaxBlockRange, getAlchemyRpcUrl } from './chains/alchemy'
import { getEthCallGasCapForEndpoint } from './chains/gas-limits'

export const ERPC_BASE_URL = 'https://rpc.morpho.dev'

export type ErpcEndpoint = 'cache' | 'realtime' | 'experimental'

export type AlchemyArgs = { apiKey: string | undefined }

export type CustomArgs = { url: string | undefined }

export type ErpcArgs = {
  secret: string | undefined
  endpoint: ErpcEndpoint
  /** Only honored when `endpoint === 'cache'`. Seconds. Sent as `X-ERPC-Cache-Max-Age`. */
  maxAge?: number
}

/**
 * The output of `rpcs.<source>.chain(chainId).with(args, opts)` — one entry in
 * an RPC failover stack. Carries the constructed URL, an http transport (when
 * the required args are present), and the recommended limits/timeouts for the
 * (chain, source) pair. Use `hasTransport` to narrow before composing failover.
 */
export type RpcCandidate = {
  /** `undefined` when required args (apiKey / secret) are missing or chain is unsupported. */
  transport: Transport | undefined
  /** Constructed URL (also `undefined` when args missing or chain unsupported). */
  url: string | undefined
  /** Recommended viem block-range cap for `eth_getLogs`-style queries. */
  maxBlockRange: number
  /** Recommended `eth_call` gas cap for this (chain, source) pair. */
  gasLimit: number | undefined
  /** Recommended fetch timeout in ms. Caller can override via `opts`. */
  timeout: number
}

export type ChainBuilder<TArgs> = {
  with: (args: TArgs, opts?: HttpTransportConfig) => RpcCandidate
}

/**
 * Configurable factory for building RPC candidates against a specific service
 * (alchemy / erpc) or any caller-supplied URL (custom). Apply per-chain with
 * `.chain(chainId)` then per-config with `.with(args, opts)`.
 */
export type RpcSource<TArgs> = {
  chain: (chainId: ChainId) => ChainBuilder<TArgs>
}

const alchemySource: RpcSource<AlchemyArgs> = {
  chain: chainId => ({
    with: ({ apiKey }, opts) => {
      const url = getAlchemyRpcUrl(chainId, apiKey)
      const timeout = 3_000
      return {
        url,
        transport: url ? http(url, { timeout, ...opts }) : undefined,
        maxBlockRange: getAlchemyMaxBlockRange(chainId) ?? 10_000,
        gasLimit: url ? getEthCallGasCapForEndpoint(chainId, url) : undefined,
        timeout
      }
    }
  })
}

const erpcSource: RpcSource<ErpcArgs> = {
  chain: chainId => ({
    with: ({ secret, endpoint, maxAge }, opts) => {
      const timeout = 30_000
      if (!secret) {
        return {
          url: undefined,
          transport: undefined,
          maxBlockRange: 10_000,
          gasLimit: undefined,
          timeout
        }
      }
      const url = `${ERPC_BASE_URL}/${endpoint}/evm/${chainId}?secret=${secret}`
      const cacheHeader =
        endpoint === 'cache' && maxAge !== undefined
          ? { 'X-ERPC-Cache-Max-Age': String(maxAge) }
          : undefined
      const fetchOptions = cacheHeader
        ? {
            ...opts?.fetchOptions,
            headers: { ...opts?.fetchOptions?.headers, ...cacheHeader }
          }
        : opts?.fetchOptions
      return {
        url,
        transport: http(url, { timeout, ...opts, fetchOptions }),
        maxBlockRange: endpoint === 'realtime' ? 10_000 : 150_000,
        gasLimit: getEthCallGasCapForEndpoint(chainId, url),
        timeout
      }
    }
  })
}

/**
 * Caller-driven URL source — wraps any URL the caller supplies (anvil forks,
 * public RPCs, custom upstreams) in an http transport with sensible defaults.
 * Always emits a transport when `url` is provided; emits `undefined` otherwise
 * so callers can `.filter(hasTransport)` to drop unconfigured candidates.
 */
const customSource: RpcSource<CustomArgs> = {
  chain: () => ({
    with: ({ url }, opts) => {
      const timeout = 30_000
      return {
        url,
        transport: url ? http(url, { timeout, ...opts }) : undefined,
        maxBlockRange: 10_000,
        gasLimit: undefined,
        timeout
      }
    }
  })
}

export const rpcs = {
  alchemy: alchemySource,
  erpc: erpcSource,
  custom: customSource
} as const

/**
 * Type guard narrowing an `RpcCandidate` to one with a non-`undefined` transport.
 * Use in `.filter(hasTransport)` to drop candidates whose env vars are unset
 * before composing failover branches.
 */
export function hasTransport(
  candidate: RpcCandidate
): candidate is RpcCandidate & { transport: NonNullable<RpcCandidate['transport']> } {
  return candidate.transport !== undefined
}

/**
 * `viem-dlc`'s `failover` returns `Transport<'viem-dlc-failover'>`, but when every
 * branch underneath is a `cache(...)` transport the underlying RPC schema is
 * identical. Override the transport `type`/`key`/`name` fields so downstream
 * code that's typed against `Transport<'viem-dlc-cache'>` keeps compiling.
 */
export function asCacheTransport(
  transport: Transport<string, unknown>
): Transport<'viem-dlc-cache'> {
  return (opts => {
    const t = transport(opts)
    return {
      ...t,
      config: {
        ...t.config,
        type: 'viem-dlc-cache' as const,
        key: 'viem-dlc-cache',
        name: '[viem-dlc] cache'
      }
    }
  }) as Transport<'viem-dlc-cache'>
}
